import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Check, Star } from 'lucide-react';
import clsx from 'clsx';
import { getPlans, createCheckoutSession, type Plan } from '../../api/client';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function formatSessionLimit(limit: number): string {
  if (limit >= 1_000_000) return `${(limit / 1_000_000).toFixed(0)}M`;
  if (limit >= 1_000) return `${(limit / 1_000).toFixed(0)}K`;
  return (limit || 0).toLocaleString();
}

export default function PlanSelectPage() {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
  });

  const sortedPlans = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);

  async function handleSelectPlan(plan: Plan) {
    setLoadingPlanId(plan.id);
    try {
      const origin = window.location.origin;
      const { checkoutUrl } = await createCheckoutSession(
        plan.id,
        `${origin}/onboarding/setup?session_id={CHECKOUT_SESSION_ID}`,
        `${origin}/onboarding/plan`,
      );
      window.location.href = checkoutUrl;
    } catch {
      setLoadingPlanId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-gray-50 px-4 py-16">
      <div className="mb-12 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
          <span className="text-xl font-bold text-white">V</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Choose your plan</h1>
        <p className="mt-2 text-sm text-gray-500">
          Start with the plan that fits your needs. Upgrade anytime.
        </p>
      </div>

      <div className="grid w-full max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
        {sortedPlans.map((plan) => {
          const isPopular = plan.slug === 'growth';
          return (
            <div
              key={plan.id}
              className={clsx(
                'relative flex flex-col rounded-2xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-md',
                isPopular ? 'border-brand-600 ring-2 ring-brand-600' : 'border-gray-200',
              )}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                    <Star className="h-3 w-3" />
                    Popular
                  </span>
                </div>
              )}

              <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-gray-900">
                  {formatPrice(plan.monthlyPriceCents)}
                </span>
                <span className="text-sm text-gray-500">/mo</span>
              </div>

              <p className="mt-2 text-sm text-gray-500">
                {formatSessionLimit(plan.monthlySessionLimit)} sessions / month
              </p>

              <ul className="mt-8 flex-1 space-y-3">
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                  Up to {plan.maxSites} {plan.maxSites === 1 ? 'site' : 'sites'}
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                  {plan.maxTeamMembers} team {plan.maxTeamMembers === 1 ? 'member' : 'members'}
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                  {plan.retentionDays}-day data retention
                </li>
                {Object.entries(plan.features)
                  .filter(([, enabled]) => enabled)
                  .map(([feature]) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                      {feature
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, (s) => s.toUpperCase())
                        .trim()}
                    </li>
                  ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan)}
                disabled={loadingPlanId !== null}
                className={clsx(
                  'mt-8 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors',
                  isPopular
                    ? 'bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50'
                    : 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 disabled:opacity-50',
                )}
              >
                {loadingPlanId === plan.id ? 'Redirecting...' : 'Get Started'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
